import type en from '../en/auth'
import type { Translation } from '../types'

/** Signing in, passwords, two-factor sign-in and the sessions of an account */
const auth: Translation<typeof en> = {
  "auth.tagline": "Plattform für Netzwerkverwaltung",
  "auth.ssoCouldNotStart": "Single Sign-On konnte nicht gestartet werden",
  "auth.loginFailed": "Anmeldung fehlgeschlagen",
  "auth.idleSignedOut": "Sie wurden nach einer Zeit ohne Aktivität abgemeldet. Melden Sie sich erneut an, um fortzufahren.",
  "auth.emailPlaceholder": "sie@unternehmen.de",
  "auth.signIn": "Anmelden",
  "auth.signInSso": "Mit Single Sign-On anmelden",
  "auth.ssoByEmailHint": "Ihre geschäftliche Adresse bestimmt, über welches Unternehmen Sie sich anmelden.",
  "auth.continueSso": "Weiter mit Single Sign-On",
  "auth.forgotPassword": "Passwort vergessen?",
  "auth.resetFailed": "Die E-Mail zum Zurücksetzen konnte nicht gesendet werden",
  "auth.setFailed": "Das Passwort konnte nicht festgelegt werden",
  "auth.resetTitle": "Passwort zurücksetzen",
  "auth.setTitle": "Legen Sie Ihr Passwort fest",
  "auth.resetSubtitle": "Geben Sie Ihre E-Mail-Adresse ein, um einen Link zum Zurücksetzen zu erhalten",
  "auth.setSubtitle": "Erstellen Sie ein sicheres Passwort für Ihr Konto",
  "auth.checkEmail": "Prüfen Sie Ihre E-Mails",
  "auth.passwordSet": "Passwort festgelegt",
  "auth.resetSent": "Wenn diese E-Mail-Adresse existiert, wurde ein Link zum Zurücksetzen gesendet.",
  "auth.accountActive": "Ihr Konto ist jetzt aktiv. Sie können sich anmelden.",
  "auth.goToLogin": "Zur Anmeldung",
  "auth.sendResetLink": "Link zum Zurücksetzen senden",
  "auth.backToLogin": "Zurück zur Anmeldung",
  "auth.newPassword": "Neues Passwort",
  "auth.confirmPassword": "Passwort bestätigen",
  "auth.check.length": "Mindestens 12 Zeichen",
  "auth.check.upper": "Großbuchstabe",
  "auth.check.lower": "Kleinbuchstabe",
  "auth.check.number": "Ziffer",
  "auth.check.match": "Passwörter stimmen überein",
  "auth.setAndActivate": "Passwort festlegen und Konto aktivieren",
  "auth.landing.title": "Wohin möchten Sie?",
  "auth.landing.signedInAs": "Angemeldet als {email}",
  "auth.landing.noAccess": "Sie haben noch keinen Zugriff auf ein Unternehmen. Bitten Sie einen Administrator, Sie hinzuzufügen.",
  "auth.landing.platform": "Plattformverwaltung",
  "auth.landing.platformSub": "Unternehmen, Lizenzen, Edge-Proxy und Einstellungen",
  "auth.landing.companyAdmin": "Unternehmensadministrator",
  "auth.landing.member": "Mitglied",
  "auth.ssoIncomplete": "Die Anmeldung konnte nicht abgeschlossen werden. Melden Sie sich erneut an.",
  "auth.backToSignIn": "Zurück zur Anmeldung",
  "auth.signingIn": "Sie werden angemeldet…",
  "auth.reauth.title": "Bestätigen Sie, dass Sie es sind",
  "auth.reauth.sso": "Für diese Änderung müssen Sie sich erneut bei Ihrem Identitätsanbieter anmelden (höchstens alle 10 Minuten). Nehmen Sie die Änderung danach noch einmal vor.",
  "auth.reauth.idpUnreachable": "Der Identitätsanbieter ist nicht erreichbar",
  "auth.reauth.password": "Für diese Änderung wird Ihr Passwort erneut benötigt. In den nächsten 10 Minuten werden Sie nicht erneut gefragt.",
  "auth.tf.startAgain": "← Neu beginnen",
  "auth.tf.setupTitle": "Zwei-Faktor-Anmeldung einrichten",
  "auth.tf.setupIntro": "Ihr Konto benötigt bei jeder Anmeldung einen Code aus einer Authenticator-App. Richten Sie diese jetzt einmalig ein.",
  "auth.tf.title": "Zwei-Faktor-Anmeldung",
  "auth.tf.enterRecovery": "Geben Sie einen Ihrer Wiederherstellungscodes ein. Jeder funktioniert nur einmal.",
  "auth.tf.enterCode": "Geben Sie den 6-stelligen Code ein, den Ihre Authenticator-App jetzt anzeigt.",
  "auth.tf.recoveryCode": "Wiederherstellungscode",
  "auth.tf.code": "Code",
  "auth.tf.verify": "Bestätigen",
  "auth.tf.useApp": "Einen Code aus der App verwenden",
  "auth.tf.lostPhone": "Telefon verloren? Verwenden Sie einen Wiederherstellungscode",
  "auth.tf.couldNotStart": "Die Einrichtung konnte nicht gestartet werden",
  "auth.tf.savedContinue": "Ich habe sie gespeichert – weiter",
  "auth.tf.saved": "Ich habe sie gespeichert",
  "auth.tf.step1": "Öffnen Sie eine Authenticator-App auf Ihrem Telefon (Google Authenticator, Microsoft Authenticator, 1Password, …).",
  "auth.tf.step2": "Scannen Sie den QR-Code oder geben Sie den Schlüssel ein.",
  "auth.tf.step3": "Geben Sie den 6-stelligen Code ein, den die App anzeigt.",
  "auth.tf.qrAlt": "QR-Code mit dem Schlüssel zum Scannen mit Ihrer Authenticator-App",
  "auth.tf.key": "Schlüssel",
  "auth.tf.codeFromApp": "Code aus der App",
  "auth.tf.turnOn": "Einschalten",
  "auth.tf.saveCodes": "Bewahren Sie diese Wiederherstellungscodes sicher auf, etwa in einem Passwort-Manager. Mit jedem können Sie sich einmal anmelden, wenn Sie Ihr Telefon verlieren. Sie werden nur jetzt angezeigt.",
  "auth.tf.recoveryCodes": "Wiederherstellungscodes",
  "auth.tf.codesCopied": "Wiederherstellungscodes kopiert",
  "auth.tf.copyHint": "Markieren und kopieren Sie die Codes oder laden Sie sie herunter.",
  "auth.prot.ssoLinked": "Single Sign-On verknüpft",
  "auth.prot.ssoLinkedText": "Sie können sich jetzt über Ihren Identitätsanbieter anmelden.",
  "auth.prot.couldNotStart": "Konnte nicht gestartet werden",
  "auth.prot.title": "Anmeldeschutz",
  "auth.prot.idleLabel": "Mich nach einer Zeit ohne Aktivität abmelden",
  "auth.prot.afterMax": "Nach {duration} (das hier erlaubte Maximum)",
  "auth.prot.after": "Nach {duration}",
  "auth.prot.never": "Nie",
  "auth.prot.staySignedIn": "Sie bleiben angemeldet, während Sie das Dashboard nicht nutzen, bis die Sitzung ihre maximale Dauer erreicht.",
  "auth.prot.signedOutAfter": "Eine offen gelassene Sitzung wird {duration} nach der letzten Nutzung abgemeldet.",
  "auth.prot.newDeviceMail": "Mir eine E-Mail senden, wenn sich mein Konto von einem bisher nicht verwendeten Gerät anmeldet",
  "auth.prot.sso": "Single Sign-On",
  "auth.prot.linked": "Verknüpft",
  "auth.prot.linkedText": "Sie können sich über den Identitätsanbieter Ihres Unternehmens anmelden.",
  "auth.prot.linkText": "Ihr Unternehmen meldet sich über seinen Identitätsanbieter an. Verknüpfen Sie Ihr Konto, um ihn zu nutzen.",
  "auth.prot.linkMine": "Mein Konto verknüpfen",
  "auth.sec.isOff": "Die Zwei-Faktor-Anmeldung ist ausgeschaltet",
  "auth.sec.isOn": "Die Zwei-Faktor-Anmeldung ist eingeschaltet",
  "auth.sec.signedOutDevice": "Auf diesem Gerät abgemeldet",
  "auth.sec.signedOutOthers": { one: "Auf {count} anderen Gerät abgemeldet", other: "Auf {count} anderen Geräten abgemeldet" },
  "auth.sec.couldNotSignOut": "Abmelden nicht möglich",
  "auth.sec.newSetNote": "Ein neuer Satz ersetzt den alten: Die alten Codes funktionieren dann nicht mehr.",
  "auth.sec.offNote": "Für die Anmeldung wird dann wieder nur Ihr Passwort benötigt.",
  "auth.sec.codeFromYourApp": "Code aus Ihrer Authenticator-App",
  "auth.sec.newCodes": "Neue Wiederherstellungscodes",
  "auth.sec.turnOff": "Ausschalten",
  "auth.sec.on": "Ein",
  "auth.sec.since": { one: "seit {date} · noch {count} Wiederherstellungscode", other: "seit {date} · noch {count} Wiederherstellungscodes" },
  "auth.sec.required": " · für Ihr Konto erforderlich",
  "auth.sec.offText": "Aus. Mit der Zwei-Faktor-Anmeldung reicht ein gestohlenes Passwort nicht aus: Für die Anmeldung ist zusätzlich ein Code aus einer App auf Ihrem Telefon nötig.",
  "auth.sec.whereSignedIn": "Wo Sie angemeldet sind",
  "auth.sec.thisDevice": "Dieses Gerät",
  "auth.sec.signedInWhen": "angemeldet {when}",
  "auth.sec.activeWhen": "aktiv {when}",
  "auth.sec.endsDate": "endet am {date}",
  "auth.sec.signOutDevice": "{device} abmelden, angemeldet {when}",
  "auth.sec.confirmSignOutAll": "Auf allen anderen Geräten abmelden?",
  "auth.sec.signOutOthers": "Andere Geräte abmelden",
  "auth.landing.client": "Veranstaltungen von {customer}",
  "auth.landing.clientOpens": "Veranstaltungen von {customer} — offen ab {when}",
  "auth.landing.clientNoEvent": "Veranstaltungen von {customer} — offen bei der nächsten Veranstaltung",
  "auth.clientWindowClosed": "Die Anmeldung ist nur während Ihrer Veranstaltung möglich",
  "auth.app.stayPrompt": "Auf diesem Telefon angemeldet bleiben",
  "auth.app.unlockReason": "Velanox entsperren",
  "auth.app.unlock": "Entsperren",
  "auth.app.changeServer": "Server wechseln",
  "auth.app.offline": "Sie sind offline",
}

export default auth
